import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles } from "lucide-react";
import { ZoneToggle } from "./MailClub";

const PRICES = { fr: { 3: 35, 6: 70, 12: 140 }, intl: { 3: 45, 6: 90, 12: 180 } };
const DURATIONS = [
  { m: 3, label: "3 mois", desc: "Un trimestre de douceurs : trois enveloppes, une par mois." },
  { m: 6, label: "6 mois", desc: "Une demi-année de rendez-vous postaux.", featured: true },
  { m: 12, label: "12 mois", desc: "Une année entière d'enveloppes, un cadeau qui accompagne les saisons." },
];


export default function MailClubGift() {
  const [params] = useSearchParams();
  const initMonths = [3, 6, 12].includes(Number(params.get("months"))) ? Number(params.get("months")) : 3;
  const initZone = params.get("zone") === "intl" ? "intl" : "fr";
  const [f, setF] = useState({
    buyer_first_name: "", buyer_last_name: "", buyer_email: "",
    recipient_first_name: "", recipient_last_name: "", recipient_email: "",
    recipient_address_line1: "", recipient_address_line2: "",
    recipient_postal_code: "", recipient_city: "", recipient_country: initZone === "fr" ? "France" : "",
    months: initMonths, dedication: "", accept_cgv: false, surprise: false, start_month: "",
  });
  const [mode, setMode] = useState(initMonths);
  const [zone, setZone] = useState(initZone);
  const [loading, setLoading] = useState(false);
  const [startOptions, setStartOptions] = useState([]);
  useEffect(() => {
    api.get("/mailclub/pricing").then(r => {
      const first = r.data?.next_month; // YYYY-MM (premier mois d'enveloppe possible)
      if (!first) return;
      const [y0, m0] = first.split("-").map(Number);
      const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
      const opts = Array.from({ length: 13 }, (_, i) => {
        const d = new Date(Date.UTC(y0, m0 - 1 + i, 1));
        const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
        return { key, label: `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}` };
      });
      setStartOptions(opts);
      setF(prev => ({ ...prev, start_month: prev.start_month || first }));
    }).catch(() => {});
  }, []);

  function set(k, v) { setF(prev => ({ ...prev, [k]: v })); }

  function selectPreset(m) {
    setMode(m);
    set("months", m);
  }

  async function submit(e) {
    e.preventDefault();
    if (!f.accept_cgv) return toast.error("Merci d'accepter les CGV.");
    if (!f.buyer_first_name || !f.buyer_email) return toast.error("Tes coordonnées sont requises.");
    if (!f.recipient_first_name || (!f.surprise && !f.recipient_email) || !f.recipient_address_line1 || !f.recipient_postal_code || !f.recipient_city) {
      return toast.error("Adresse postale complète du destinataire requise.");
    }
    if (![3, 6, 12].includes(f.months)) {
      return toast.error("Choisis 3, 6 ou 12 mois.");
    }
    setLoading(true);
    try {
      const r = await api.post("/mailclub/gift", { ...f, zone, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue");
      setLoading(false);
    }
  }

  const activeMonths = f.months;
  const activePrice = PRICES[zone][activeMonths] || 0;
  const currentDesc = DURATIONS.find(d => d.m === mode)?.desc || "";
  const ctaLabel = activeMonths === 1 ? "1 mois" : `${activeMonths} mois`;

  return (
    <div data-testid="mailclub-gift-page" className="max-w-3xl mx-auto px-6 md:px-12 py-16">
      <Link to="/club-de-lettres" className="text-sm text-[#A3876A] hover:underline inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={14}/> Retour
      </Link>
      <p className="font-hand text-xl text-[#A3876A] mb-2">Un joli geste</p>
      <h1 className="font-serif text-3xl md:text-4xl mb-3">Offrir le Club de Lettres</h1>
      <p className="text-[#4A4036] mb-10 italic">
        Trois, six ou douze enveloppes remplies de douceurs et de petites attentions, qui arriveront dans la boîte aux lettres d'une personne qui t'est chère. Réglé en une fois, sans renouvellement automatique : la première enveloppe part le mois suivant l'achat, autour du 10.
      </p>

      <form onSubmit={submit} className="space-y-10">
        {/* Destination */}
        <div>
          <p className="font-hand text-lg text-[#A3876A] mb-3">Où part l'enveloppe ?</p>
          <ZoneToggle zone={zone} setZone={z => { setZone(z); set("recipient_country", z === "fr" ? "France" : ""); }} testId="mc-gift-zone"/>
          <p className="text-xs text-[#7C7064] mt-2">{zone === "fr" ? "Envoi en France métropolitaine inclus." : "Envoi hors France métropolitaine inclus — tarif au prorata de 15 € par mois."} Le tarif doit correspondre au pays de l'adresse du destinataire.</p>
        </div>
        {/* Durée */}
        <div>
          <p className="font-hand text-lg text-[#A3876A] mb-4">Quelle durée ?</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {DURATIONS.map(d => (
              <label key={d.m} data-testid={`mc-gift-months-${d.m}`}
                className={`p-4 border cursor-pointer text-center transition-colors relative ${mode === d.m ? "border-[#A3876A] bg-[#FDFBF7]" : "border-[#E8DCC8] bg-[#F5F0E6] hover:bg-[#FDFBF7]"}`}>
                <input type="radio" name="months" value={d.m} checked={mode === d.m}
                  onChange={() => selectPreset(d.m)} className="sr-only"/>
                {d.featured && <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] bg-[#A3876A] text-white px-2 py-0.5 uppercase tracking-wider">populaire</span>}
                <p className="font-serif text-lg text-[#2C2621]">{d.label}</p>
                <p className="font-serif text-2xl mt-1">{PRICES[zone][d.m]}€</p>
              </label>
            ))}
          </div>

          <p className="text-sm text-[#7C7064] mt-3 italic">{currentDesc}</p>

          <div className="mt-8" data-testid="mc-gift-start-block">
            <p className="font-hand text-lg text-[#A3876A] mb-3">À partir de quel mois ?</p>
            <div className="flex flex-wrap gap-2">
              {startOptions.map(o => (
                <button key={o.key} type="button" data-testid={`mc-gift-start-${o.key}`} onClick={() => set("start_month", o.key)}
                  className={`px-4 py-2 text-sm border transition-colors ${f.start_month === o.key ? "bg-[#C35A3E] text-[#FDFBF7] border-[#C35A3E]" : "bg-white border-[#E8DCC8] text-[#4A4036] hover:border-[#C35A3E]"}`}>
                  {o.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#7C7064] mt-2 italic">
              {f.start_month === startOptions[0]?.key
                ? "Première enveloppe dès le prochain envoi, autour du 10."
                : `Première enveloppe en ${startOptions.find(o => o.key === f.start_month)?.label || ""}, autour du 10 — parfait pour viser un anniversaire ou Noël.`}
            </p>
          </div>
        </div>

        {/* Acheteur */}
        <div className="border-t border-[#E8DCC8] pt-6">
          <p className="font-hand text-lg text-[#A3876A] mb-4">Tes coordonnées</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <input required data-testid="mc-gift-buyer-fn" placeholder="Ton prénom"
              value={f.buyer_first_name} onChange={e => set("buyer_first_name", e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
            <input required data-testid="mc-gift-buyer-ln" placeholder="Ton nom"
              value={f.buyer_last_name} onChange={e => set("buyer_last_name", e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
          </div>
          <input required type="email" data-testid="mc-gift-buyer-email" placeholder="Ton email (confirmation)"
            value={f.buyer_email} onChange={e => set("buyer_email", e.target.value)}
            className="w-full mt-4 px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
        </div>

        {/* Destinataire */}
        <div className="border-t border-[#E8DCC8] pt-6">
          <p className="font-hand text-lg text-[#A3876A] mb-4">À qui offres-tu ?</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <input required data-testid="mc-gift-rec-fn" placeholder="Prénom du destinataire"
              value={f.recipient_first_name} onChange={e => set("recipient_first_name", e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
            <input required data-testid="mc-gift-rec-ln" placeholder="Nom du destinataire"
              value={f.recipient_last_name} onChange={e => set("recipient_last_name", e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
          </div>
          <div className="mt-4 p-4 bg-[#F6E3D6] border border-[#E8C9B5]">
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid="mc-gift-surprise" type="checkbox" checked={f.surprise} onChange={e => set("surprise", e.target.checked)} className="mt-1 accent-[#C35A3E]"/>
              <span>
                <span className="block font-hand text-xl text-[#C35A3E] leading-none mb-1">Chut, c'est une surprise</span>
                <span className="block text-sm text-[#4A4036] leading-relaxed">La personne n'est pas prévenue. Tu reçois par email une <strong>carte cadeau à imprimer ou à transférer</strong>, avec ta dédicace — c'est toi qui annonces la nouvelle, quand tu veux.</span>
              </span>
            </label>
          </div>
          {!f.surprise && (
            <>
              <input required type="email" data-testid="mc-gift-rec-email" placeholder="Email du destinataire"
                value={f.recipient_email} onChange={e => set("recipient_email", e.target.value)}
                className="w-full mt-4 px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
              <p className="text-xs text-[#7C7064] mt-2 italic">Une petite annonce lui sera envoyée par email dès ton paiement, avec ta dédicace.</p>
            </>
          )}

          <div className="mt-6 space-y-4">
            <p className="text-xs uppercase tracking-wider text-[#7C7064]">Adresse postale du destinataire</p>
            <input required data-testid="mc-gift-l1" placeholder="Adresse (12 rue…)"
              value={f.recipient_address_line1} onChange={e => set("recipient_address_line1", e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
            <input data-testid="mc-gift-l2" placeholder="Complément (bâtiment, appartement…)"
              value={f.recipient_address_line2} onChange={e => set("recipient_address_line2", e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
            <div className="grid grid-cols-3 gap-3">
              <input required data-testid="mc-gift-cp" placeholder="Code postal"
                value={f.recipient_postal_code} onChange={e => set("recipient_postal_code", e.target.value)}
                className="px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
              <input required data-testid="mc-gift-city" placeholder="Ville"
                value={f.recipient_city} onChange={e => set("recipient_city", e.target.value)}
                className="col-span-2 px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
            </div>
            <input data-testid="mc-gift-country" placeholder="Pays"
              value={f.recipient_country} onChange={e => set("recipient_country", e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
          </div>
        </div>

        {/* Dédicace */}
        <div className="border-t border-[#E8DCC8] pt-6">
          <p className="font-hand text-lg text-[#A3876A] mb-4">Un mot doux (facultatif)</p>
          <textarea data-testid="mc-gift-dedication" rows={3} maxLength={280}
            placeholder="Un petit mot qui accompagnera l'annonce du cadeau…"
            value={f.dedication} onChange={e => set("dedication", e.target.value)}
            className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
          <p className="text-xs text-[#7C7064] text-right">{f.dedication.length}/280</p>
        </div>

        {/* CGV */}
        <div className="border-t border-[#E8DCC8] pt-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input data-testid="mc-gift-cgv" type="checkbox" required checked={f.accept_cgv}
              onChange={e => set("accept_cgv", e.target.checked)} className="mt-1 accent-[#A3876A]"/>
            <span className="text-sm text-[#4A4036] leading-relaxed">
              J'accepte les <Link to="/cgv" className="underline hover:text-[#A3876A]">CGV</Link> et la <Link to="/confidentialite" className="underline hover:text-[#A3876A]">politique de confidentialité</Link>. *
            </span>
          </label>
        </div>

        <button data-testid="mc-gift-submit" disabled={loading} type="submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#2C2621] text-[#F5F0E6] px-8 py-4 hover:bg-[#1a1613] transition-colors disabled:opacity-60">
          <Sparkles size={18}/>
          {loading ? "Redirection…" : `J'offre ${ctaLabel} — ${activePrice}€`}
        </button>
        <p className="text-xs text-center text-[#7C7064] italic">
          Paiement unique via Stripe. Aucun renouvellement automatique — le cadeau s'arrête après {ctaLabel}.
        </p>
      </form>
    </div>
  );
}
